import { useState } from 'react';
import { useApp } from '../state/store';
import { back, navigate } from '../lib/router';
import { C, R, font } from '../theme/tokens';
import { myActivities, progress, roleOf, tasksOf } from '../state/selectors';
import { canAcceptOffers, getSession } from '../services/auth';
import { Avatar, BackPill, Btn, Card, CardTitle, Chip, ChipRow, DarkCard, EmptyState, Eyebrow, Mono, ProgressBar, inputStyle, safeTop } from '../components/ui';

export function Collaboration({ id }: { id: string }) {
  const { state: { data }, act } = useApp();
  const [draft, setDraft] = useState('');
  const mine = myActivities(data);
  const a = data.activities[id];
  if (!a || roleOf(data, id) === null) {
    return (
      <div style={{ paddingTop: safeTop(12) }}>
        <div style={{ padding: '0 16px' }}><BackPill onClick={back} /></div>
        <EmptyState title="Collaboration is for team members." body="Offer help on the activity to join the team." action={<Btn variant="primary" onClick={() => navigate({ name: 'activity', id })}>Open activity</Btn>} />
      </div>
    );
  }
  const p = progress(data, a.id);
  const chat = data.chats.filter((m) => m.activityId === a.id);
  const offers = data.offers.filter((o) => o.activityId === a.id);
  const posted = Object.keys(data.helpPosted).filter((k) => k.startsWith(`${a.id}|`)).map((k) => k.split('|')[1]);
  const asks = Array.from(new Set([...a.needs, ...posted]));
  const canAccept = canAcceptOffers(getSession(), a);
  const short = (n: string) => n.replace('London Winter Community Campaign', 'Winter Campaign');
  const send = () => {
    act.sendMessage(a.id, draft);
    setDraft('');
  };

  return (
    <div className="slide-in">
      <div style={{ padding: `${safeTop(12)} 16px 0`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <BackPill onClick={back} />
        <Mono size={10} style={{ textAlign: 'right' }}>{`${short(a.name).toUpperCase()} · TEAM OF ${a.team.length}`}</Mono>
      </div>
      <h1 style={{ margin: 0, padding: '16px 20px 0', font: font(800, 48, 0.88), letterSpacing: '-.055em', hyphens: 'manual' }}>Collab&shy;oration</h1>
      <ChipRow padding="14px 20px 0">
        {mine.map((x) => <Chip key={x.id} label={short(x.name)} selected={x.id === a.id} height={38} onClick={() => navigate({ name: 'collab', id: x.id }, { replace: true })} />)}
      </ChipRow>

      <DarkCard style={{ margin: '16px 12px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Eyebrow color={C.rim} style={{ fontSize: '0.5625rem' }}>Shared responsibilities</Eyebrow>
          <Mono color={C.white}>{p.total ? `${p.done} / ${p.total} tasks` : 'NO TASKS YET'}</Mono>
        </div>
        {a.team.map((m) => {
          const t = data.ambassadors[m.ambassadorId];
          if (!t) return null;
          const own = tasksOf(data, a.id).filter((x) => x.ownerId === t.id);
          const d = own.filter((x) => x.done).length;
          return (
            <button key={t.id} type="button" onClick={() => (t.id === data.me ? navigate({ name: 'me' }) : navigate({ name: 'profile', id: t.id }))}
              style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 12, alignItems: 'center', border: 0, background: 'transparent', padding: 0, textAlign: 'left', color: C.white, minHeight: 44 }}>
              <Avatar person={t} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ font: font(600, 14) }}>{t.id === data.me ? 'You' : t.name}</span><Mono color={C.mutedDark}>{own.length ? `${d} / ${own.length}` : '—'}</Mono></span>
                <span style={{ font: font(400, 11), color: C.mutedDark }}>{m.role}</span>
                <ProgressBar ratio={own.length ? d / own.length : 0} dark height={5} label={`${t.name} progress`} />
              </span>
            </button>
          );
        })}
        <Btn variant="primary" full onClick={() => act.openSheet({ kind: 'update', activityId: a.id })} style={{ height: 48, font: font(700, 15) }}>Update progress</Btn>
      </DarkCard>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <CardTitle right={<button type="button" onClick={() => act.openSheet({ kind: 'help', activityId: a.id })} style={{ border: 0, background: 'transparent', color: C.violet, font: font(600, 13), padding: '10px 0' }}>Request help ›</button>}>Help</CardTitle>
        {asks.map((r) => {
          const on = posted.includes(r);
          return (
            <div key={r} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: 12, borderRadius: R.thumb, background: on ? C.soft : C.amberBg, border: `1px solid ${on ? C.accentBorder : C.amberBorder}` }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ font: font(600, 14), color: on ? C.violet : C.amber }}>{r}</span>
                <span style={{ font: font(400, 11), color: C.grey }}>{on ? 'Live on the UGAIN Pulse' : 'Open role · not yet posted'}</span>
              </span>
              <span style={{ font: font(700, 9), letterSpacing: '.12em', color: on ? C.violet : C.amber }}>{on ? 'POSTED' : 'OPEN'}</span>
            </div>
          );
        })}
        {offers.map((o) => {
          const who = data.ambassadors[o.ambassadorId];
          const accepted = o.status === 'accepted';
          return (
            <div key={o.id} style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 12, alignItems: 'center', padding: 12, borderRadius: R.thumb, border: `1px solid ${C.accentBorder}`, background: C.soft }}>
              <Avatar person={who} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ font: font(500, 13, 1.35) }}><b>{who.name}</b>{` offered to help: ${o.role.toLowerCase()}`}</span>
                <span style={{ display: 'flex', gap: 6 }}>
                  {canAccept ? (
                    <Btn size="xs" variant={accepted ? 'success' : 'primary'} disabled={accepted} onClick={() => act.acceptOffer(o.id)}>{accepted ? 'Joined team ✓' : 'Accept'}</Btn>
                  ) : (
                    <Mono>{accepted ? 'JOINED' : 'ORGANISER CONFIRMS'}</Mono>
                  )}
                  <Btn size="xs" onClick={() => navigate({ name: 'profile', id: who.id })}>Profile</Btn>
                </span>
              </span>
            </div>
          );
        })}
        {!asks.length && !offers.length && <span style={{ font: font(400, 13), color: C.grey }}>No open requests for this activity.</span>}
      </Card>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <CardTitle right={<Mono size={10}>TEAM ONLY</Mono>}>Team chat</CardTitle>
        <div role="log" aria-label="Team chat" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {chat.map((m) => {
            const own = m.authorId === data.me;
            return (
              <div key={m.id} className="fade" style={{ display: 'flex', flexDirection: 'column', alignItems: own ? 'flex-end' : 'flex-start', gap: 3 }}>
                <Mono size={10}>{own ? 'YOU' : data.ambassadors[m.authorId]?.name.toUpperCase()}</Mono>
                <span style={{ maxWidth: '78%', padding: '10px 14px', borderRadius: 18, background: own ? C.violet : C.divider, color: own ? C.white : C.ink, font: font(400, 14, 1.4) }}>{m.text}</span>
              </div>
            );
          })}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <input aria-label="Message the team" value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={500} placeholder="Message the team" style={{ ...inputStyle, height: 48, borderRadius: R.pill, flex: 1, minWidth: 0 }} />
          <Btn type="submit" variant="ink" disabled={!draft.trim()} style={{ height: 48 }}>Send</Btn>
        </form>
      </Card>
    </div>
  );
}
